import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, resolveImg, formatError } from "@/lib/api";
import { X, ArrowUp, ArrowDown, Check, ImageOff, GripVertical } from "lucide-react";
import { useT } from "@/i18n/LanguageContext";
import { useAdmin } from "@/context/AdminContext";
import ProductImagePicker from "@/components/ProductImagePicker";

const MAX_FEATURED = 3;
const MAX_GALLERY = 300;
const TAGLINE_LANGS = ["ro", "ru", "en"];
const TAGLINE_MAX = 80;
const EMPTY_TAGLINE = { ro: "", ru: "", en: "" };

export default function AdminHomepage() {
    const t = useT();
    const { unsavedRef } = useAdmin();
    const [allProducts, setAllProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [featuredCategory, setFeaturedCategory] = useState("");
    const [selectedIds, setSelectedIds] = useState([]);
    const [featuredCatProducts, setFeaturedCatProducts] = useState([]);
    const [galleryCategory, setGalleryCategory] = useState("");
    const [galleryIds, setGalleryIds] = useState([]);
    const [galleryProducts, setGalleryProducts] = useState([]);
    const [heroImage, setHeroImage] = useState("");
    const [experienceImage, setExperienceImage] = useState("");
    const [aboutIntroImage, setAboutIntroImage] = useState("");
    const [aboutVisitImage, setAboutVisitImage] = useState("");
    const [heroTagline, setHeroTagline] = useState(EMPTY_TAGLINE);
    const [saving, setSaving] = useState(false);
    const [loaded, setLoaded] = useState(false);
    // Last saved state — powers the dirty indicator and the Undo button.
    const [savedState, setSavedState] = useState(null);

    useEffect(() => {
        api.get("/homepage").then((r) => {
            const fc = r.data.featured_category_id ?? "";
            const fids = r.data.featured_product_ids ?? [];
            const gc = r.data.gallery_category_id ?? "";
            const gids = r.data.gallery_product_ids ?? [];
            const hi = r.data.hero_image ?? "";
            const ei = r.data.experience_image ?? "";
            const ai = r.data.about_intro_image ?? "";
            const av = r.data.about_visit_image ?? "";
            const ht = { ...EMPTY_TAGLINE, ...(r.data.hero_tagline ?? {}) };
            setFeaturedCategory(fc);
            setSelectedIds(fids);
            setGalleryCategory(gc);
            setGalleryIds(gids);
            setHeroImage(hi);
            setExperienceImage(ei);
            setAboutIntroImage(ai);
            setAboutVisitImage(av);
            setHeroTagline(ht);
            setSavedState({ fc, fids, gc, gids, hi, ei, ai, av, ht });
            setLoaded(true);
        }).catch(() => setLoaded(true));
        api.get("/products", { params: { page_size: 1000 } })
            .then((r) => setAllProducts(r.data.items ?? []))
            .catch(() => toast.error("Failed to load products"));
        api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    }, []);

    // Products of the chosen "Piese alese" source collection
    useEffect(() => {
        if (!featuredCategory) { setFeaturedCatProducts([]); return; }
        api.get("/products", { params: { category: featuredCategory, page_size: 1000 } })
            .then((r) => setFeaturedCatProducts(r.data.items ?? []))
            .catch(() => setFeaturedCatProducts([]));
    }, [featuredCategory]);

    // Products of the chosen gallery source collection
    useEffect(() => {
        if (!galleryCategory) { setGalleryProducts([]); return; }
        api.get("/products", { params: { category: galleryCategory, page_size: 1000 } })
            .then((r) => setGalleryProducts(r.data.items ?? []))
            .catch(() => setGalleryProducts([]));
    }, [galleryCategory]);

    const byId = useMemo(() => Object.fromEntries(allProducts.map((p) => [p.id, p])), [allProducts]);
    const selected = selectedIds.map((id) => byId[id]).filter(Boolean);

    const toggleFeaturedProduct = (id) =>
        setSelectedIds((ids) => {
            if (ids.includes(id)) return ids.filter((x) => x !== id);
            if (ids.length >= MAX_FEATURED) {
                toast.error(t("admin.home.featured.max"));
                return ids;
            }
            return [...ids, id];
        });

    const removeProduct = (id) => setSelectedIds((ids) => ids.filter((x) => x !== id));

    const moveProduct = (idx, dir) => {
        setSelectedIds((ids) => {
            const next = [...ids];
            const j = idx + dir;
            if (j < 0 || j >= next.length) return ids;
            [next[idx], next[j]] = [next[j], next[idx]];
            return next;
        });
    };

    // Drag-and-drop reordering of the selected list (live reorder while dragging).
    const [dragIdx, setDragIdx] = useState(null);

    const onRowDragOver = (idx) => {
        if (dragIdx === null || dragIdx === idx) return;
        setSelectedIds((ids) => {
            const next = [...ids];
            const [moved] = next.splice(dragIdx, 1);
            next.splice(idx, 0, moved);
            return next;
        });
        setDragIdx(idx);
    };

    const toggleGalleryProduct = (id) =>
        setGalleryIds((ids) => {
            if (ids.includes(id)) return ids.filter((x) => x !== id);
            if (ids.length >= MAX_GALLERY) {
                toast.error(t("admin.home.gallery.max", { n: MAX_GALLERY }));
                return ids;
            }
            return [...ids, id];
        });

    const selectAllGallery = () => {
        setGalleryIds(galleryProducts.slice(0, MAX_GALLERY).map((p) => p.id));
        if (galleryProducts.length > MAX_GALLERY) {
            toast.info(t("admin.home.gallery.max", { n: MAX_GALLERY }));
        }
    };

    const dirty = savedState !== null && JSON.stringify({
        fc: featuredCategory, fids: selectedIds, gc: galleryCategory, gids: galleryIds,
        hi: heroImage, ei: experienceImage, ai: aboutIntroImage, av: aboutVisitImage, ht: heroTagline,
    }) !== JSON.stringify(savedState);

    // Let the admin layout warn (and offer to save) before navigating away with unsaved changes.
    useEffect(() => {
        unsavedRef.current = dirty ? { save } : null;
    });
    useEffect(() => () => { unsavedRef.current = null; }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Warn on tab close / refresh too.
    useEffect(() => {
        if (!dirty) return;
        const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ""; };
        window.addEventListener("beforeunload", onBeforeUnload);
        return () => window.removeEventListener("beforeunload", onBeforeUnload);
    }, [dirty]);

    const undo = () => {
        if (!savedState) return;
        setFeaturedCategory(savedState.fc);
        setSelectedIds(savedState.fids);
        setGalleryCategory(savedState.gc);
        setGalleryIds(savedState.gids);
        setHeroImage(savedState.hi);
        setExperienceImage(savedState.ei);
        setAboutIntroImage(savedState.ai);
        setAboutVisitImage(savedState.av);
        setHeroTagline(savedState.ht);
    };

    const save = async () => {
        setSaving(true);
        try {
            // Drop gallery selections that no longer belong to the source collection.
            const validGalleryIds = galleryProducts.length
                ? galleryIds.filter((id) => galleryProducts.some((p) => p.id === id))
                : galleryIds;
            const galleryToSave = galleryCategory ? validGalleryIds : [];
            const taglineToSave = Object.fromEntries(TAGLINE_LANGS.map((l) => [l, heroTagline[l].trim()]));
            await api.put("/homepage", {
                featured_category_id: featuredCategory,
                featured_product_ids: selectedIds,
                gallery_category_id: galleryCategory,
                gallery_product_ids: galleryToSave,
                hero_image: heroImage,
                experience_image: experienceImage,
                about_intro_image: aboutIntroImage,
                about_visit_image: aboutVisitImage,
                hero_tagline: taglineToSave,
            });
            setGalleryIds(galleryToSave);
            setHeroTagline(taglineToSave);
            setSavedState({
                fc: featuredCategory, fids: selectedIds, gc: galleryCategory, gids: galleryToSave,
                hi: heroImage, ei: experienceImage, ai: aboutIntroImage, av: aboutVisitImage, ht: taglineToSave,
            });
            toast.success(t("admin.home.saved"));
        } catch (err) {
            toast.error(formatError(err?.response?.data?.detail) || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="p-8 md:p-12" data-testid="admin-homepage">
            {/* Sticky so Save stays reachable however far down the page you are. */}
            <header className="sticky top-0 z-30 -mx-8 md:-mx-12 px-8 md:px-12 py-5 mb-10 bg-melilux-surface/95 backdrop-blur border-b border-white/10 flex items-center justify-between flex-wrap gap-4">
                <div>
                    <p className="overline">{t("admin.home.overline")}</p>
                    <h1 className="font-serif text-4xl md:text-5xl mt-2">{t("admin.home.title")}</h1>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                    {dirty && (
                        <>
                            <span className="text-[0.65rem] uppercase tracking-[0.25em] text-melilux-gold/80" data-testid="homepage-unsaved">
                                {t("admin.home.unsaved")}
                            </span>
                            <button
                                onClick={undo}
                                data-testid="homepage-undo"
                                className="border border-white/15 px-6 py-3 text-[0.7rem] uppercase tracking-[0.28em] hover:border-melilux-cream/40 transition-colors"
                            >
                                {t("admin.home.undo")}
                            </button>
                        </>
                    )}
                    <button
                        onClick={save}
                        disabled={saving || !loaded || !dirty}
                        data-testid="homepage-save"
                        className="bg-melilux-gold text-melilux-bg px-6 py-3 text-[0.7rem] uppercase tracking-[0.28em] hover:bg-melilux-goldBright transition-colors disabled:opacity-40"
                    >
                        {saving ? t("admin.home.saving") : t("admin.home.save")}
                    </button>
                </div>
            </header>

            {/* Text over the hero photo */}
            <section className="border border-white/10 bg-melilux-bg p-6 md:p-8 mb-10" data-testid="homepage-tagline">
                <h2 className="font-serif text-2xl">{t("admin.home.tagline.title")}</h2>
                <p className="text-[0.7rem] uppercase tracking-[0.2em] text-melilux-cream/45 mt-2 max-w-2xl leading-relaxed normal-case">
                    {t("admin.home.tagline.hint")}
                </p>
                <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
                    {TAGLINE_LANGS.map((l) => (
                        <label key={l} className="block">
                            <span className="flex items-baseline justify-between text-[0.65rem] uppercase tracking-[0.28em] text-melilux-cream/55">
                                {l}
                                <span className="tracking-normal text-melilux-cream/35">{heroTagline[l].length} / {TAGLINE_MAX}</span>
                            </span>
                            <input
                                type="text"
                                value={heroTagline[l]}
                                maxLength={TAGLINE_MAX}
                                onChange={(e) => setHeroTagline((cur) => ({ ...cur, [l]: e.target.value }))}
                                data-testid={`homepage-tagline-${l}`}
                                className="mt-3 w-full bg-melilux-bg border border-white/15 px-4 py-3 font-serif italic text-xl text-melilux-cream focus:border-melilux-gold outline-none"
                            />
                        </label>
                    ))}
                </div>
            </section>

            {/* Homepage images */}
            <section className="border border-white/10 bg-melilux-bg p-6 md:p-8 mb-10" data-testid="homepage-images">
                <h2 className="font-serif text-2xl">{t("admin.home.images.title")}</h2>
                <p className="text-[0.7rem] uppercase tracking-[0.2em] text-melilux-cream/45 mt-2 max-w-2xl leading-relaxed normal-case">
                    {t("admin.home.images.hint")}
                </p>
                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-8">
                    <ImagePicker
                        label={t("admin.home.images.hero")}
                        value={heroImage}
                        onChange={setHeroImage}
                        aspect="aspect-[16/9]"
                        testid="homepage-hero-image"
                        t={t}
                    />
                    <ImagePicker
                        label={t("admin.home.images.experience")}
                        value={experienceImage}
                        onChange={setExperienceImage}
                        aspect="aspect-[3/4]"
                        testid="homepage-experience-image"
                        t={t}
                    />
                </div>
            </section>

            {/* About page images */}
            <section className="border border-white/10 bg-melilux-bg p-6 md:p-8 mb-10" data-testid="about-images">
                <h2 className="font-serif text-2xl">{t("admin.about.images.title")}</h2>
                <p className="text-[0.7rem] uppercase tracking-[0.2em] text-melilux-cream/45 mt-2 max-w-2xl leading-relaxed normal-case">
                    {t("admin.about.images.hint")}
                </p>
                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-8">
                    <ImagePicker
                        label={t("admin.about.images.intro")}
                        value={aboutIntroImage}
                        onChange={setAboutIntroImage}
                        aspect="aspect-[3/4]"
                        testid="about-intro-image"
                        t={t}
                    />
                    <ImagePicker
                        label={t("admin.about.images.visit")}
                        value={aboutVisitImage}
                        onChange={setAboutVisitImage}
                        aspect="aspect-[5/4]"
                        testid="about-visit-image"
                        t={t}
                    />
                </div>
            </section>

            {/* Selected pieces */}
            <section className="border border-white/10 bg-melilux-bg p-6 md:p-8 mb-10" data-testid="homepage-featured">
                <h2 className="font-serif text-2xl">{t("admin.home.featured.title")}</h2>
                <p className="text-[0.7rem] uppercase tracking-[0.2em] text-melilux-cream/45 mt-2 max-w-2xl leading-relaxed normal-case">
                    {t("admin.home.featured.hint")}
                </p>

                {selected.length === 0 ? (
                    <p className="mt-6 text-sm text-melilux-cream/50 italic" data-testid="homepage-featured-empty">
                        {t("admin.home.featured.empty")}
                    </p>
                ) : (
                    <div className="mt-6 space-y-3">
                        {selected.map((p, idx) => (
                            <div
                                key={p.id}
                                draggable
                                onDragStart={(e) => { setDragIdx(idx); e.dataTransfer.effectAllowed = "move"; }}
                                onDragOver={(e) => { e.preventDefault(); onRowDragOver(idx); }}
                                onDrop={(e) => e.preventDefault()}
                                onDragEnd={() => setDragIdx(null)}
                                className={`flex items-center gap-4 border bg-white/[0.02] p-3 cursor-grab active:cursor-grabbing transition-colors ${
                                    dragIdx === idx ? "border-melilux-gold opacity-60" : "border-white/10"
                                }`}
                                data-testid={`homepage-featured-${p.id}`}
                            >
                                <GripVertical size={15} strokeWidth={1.5} className="text-melilux-cream/30 shrink-0" />
                                <span className="font-serif text-2xl text-melilux-gold/60 w-8 text-center shrink-0">0{idx + 1}</span>
                                {p.images?.[0] && (
                                    <img src={resolveImg(p.images[0], 320)} alt="" draggable={false} className="w-14 h-14 object-cover shrink-0" />
                                )}
                                <div className="min-w-0 flex-1">
                                    <p className="font-serif text-lg truncate">{p.name}</p>
                                    <p className="text-[0.62rem] uppercase tracking-[0.22em] text-melilux-cream/45 truncate">
                                        {p.category_name || p.room}{!p.visible && " · hidden"}
                                    </p>
                                </div>
                                <div className="flex items-center gap-1 shrink-0">
                                    <button onClick={() => moveProduct(idx, -1)} disabled={idx === 0} className="p-2 border border-white/10 hover:border-melilux-gold hover:text-melilux-gold disabled:opacity-25 transition-colors" aria-label="Move up">
                                        <ArrowUp size={13} strokeWidth={1.5} />
                                    </button>
                                    <button onClick={() => moveProduct(idx, 1)} disabled={idx === selected.length - 1} className="p-2 border border-white/10 hover:border-melilux-gold hover:text-melilux-gold disabled:opacity-25 transition-colors" aria-label="Move down">
                                        <ArrowDown size={13} strokeWidth={1.5} />
                                    </button>
                                    <button onClick={() => removeProduct(p.id)} className="p-2 border border-white/10 hover:border-destructive hover:text-destructive transition-colors" data-testid={`homepage-featured-remove-${p.id}`} aria-label="Remove">
                                        <X size={13} strokeWidth={1.5} />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                <div className="mt-6 flex flex-wrap items-center gap-4">
                    <label className="text-[0.65rem] uppercase tracking-[0.28em] text-melilux-cream/55">
                        {t("admin.home.gallery.category")}
                    </label>
                    <select
                        value={featuredCategory}
                        onChange={(e) => setFeaturedCategory(e.target.value)}
                        data-testid="homepage-featured-category"
                        className="bg-melilux-bg border border-white/15 px-4 py-3 text-sm text-melilux-cream focus:border-melilux-gold outline-none min-w-[240px]"
                    >
                        <option value="">{t("admin.home.gallery.pickCategory")}</option>
                        {categories.map((c) => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                    </select>
                    {featuredCategory && (
                        <span className="text-[0.68rem] uppercase tracking-[0.22em] text-melilux-cream/45">
                            {t("admin.home.gallery.selected", { n: selectedIds.length })} / {MAX_FEATURED}
                        </span>
                    )}
                </div>

                {featuredCategory && (
                    featuredCatProducts.length === 0 ? (
                        <p className="mt-6 text-sm text-melilux-cream/50 italic">{t("admin.home.gallery.noProducts")}</p>
                    ) : (
                        <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                            {featuredCatProducts.map((p) => {
                                const order = selectedIds.indexOf(p.id);
                                const active = order >= 0;
                                return (
                                    <button
                                        key={p.id}
                                        type="button"
                                        onClick={() => toggleFeaturedProduct(p.id)}
                                        data-testid={`homepage-featured-toggle-${p.id}`}
                                        className={`relative text-left border transition-colors group ${
                                            active ? "border-melilux-gold" : "border-white/10 hover:border-white/30"
                                        }`}
                                    >
                                        <div className={`aspect-square bg-melilux-surface ${active ? "" : "opacity-60 group-hover:opacity-90"} transition-opacity`}>
                                            {p.images?.[0] && (
                                                <img src={resolveImg(p.images[0], 320)} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                                            )}
                                        </div>
                                        {active && (
                                            <span className="absolute top-2 left-2 flex items-center gap-1 bg-melilux-gold text-melilux-bg px-2 py-1 text-[0.6rem] uppercase tracking-[0.2em] font-medium">
                                                <Check size={11} strokeWidth={2.5} /> {order + 1}
                                            </span>
                                        )}
                                        <p className="px-2 py-2 text-xs truncate text-melilux-cream/80">{p.name}</p>
                                    </button>
                                );
                            })}
                        </div>
                    )
                )}
            </section>

            {/* Projects gallery — products from a chosen collection */}
            <section className="border border-white/10 bg-melilux-bg p-6 md:p-8" data-testid="homepage-gallery">
                <h2 className="font-serif text-2xl">{t("admin.home.gallery.title")}</h2>
                <p className="text-[0.7rem] uppercase tracking-[0.2em] text-melilux-cream/45 mt-2 max-w-2xl leading-relaxed normal-case">
                    {t("admin.home.gallery.hint")}
                </p>

                <div className="mt-6 flex flex-wrap items-center gap-4">
                    <label className="text-[0.65rem] uppercase tracking-[0.28em] text-melilux-cream/55">
                        {t("admin.home.gallery.category")}
                    </label>
                    <select
                        value={galleryCategory}
                        onChange={(e) => { setGalleryCategory(e.target.value); setGalleryIds([]); }}
                        data-testid="homepage-gallery-category"
                        className="bg-melilux-bg border border-white/15 px-4 py-3 text-sm text-melilux-cream focus:border-melilux-gold outline-none min-w-[240px]"
                    >
                        <option value="">{t("admin.home.gallery.pickCategory")}</option>
                        {categories.map((c) => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                    </select>

                    {galleryCategory && galleryProducts.length > 0 && (
                        <div className="flex items-center gap-4 text-[0.68rem] uppercase tracking-[0.22em]">
                            <button
                                onClick={selectAllGallery}
                                data-testid="homepage-gallery-select-all"
                                className="text-melilux-gold link-underline"
                            >
                                {t("admin.home.gallery.selectAll")}
                            </button>
                            <button
                                onClick={() => setGalleryIds([])}
                                data-testid="homepage-gallery-clear"
                                className="text-melilux-cream/55 link-underline"
                            >
                                {t("admin.home.gallery.clear")}
                            </button>
                            <span className="text-melilux-cream/45">{t("admin.home.gallery.selected", { n: galleryIds.length })}</span>
                        </div>
                    )}
                </div>

                {!galleryCategory ? (
                    <p className="mt-6 text-sm text-melilux-cream/50 italic" data-testid="homepage-gallery-empty">
                        {t("admin.home.gallery.empty")}
                    </p>
                ) : galleryProducts.length === 0 ? (
                    <p className="mt-6 text-sm text-melilux-cream/50 italic">{t("admin.home.gallery.noProducts")}</p>
                ) : (
                    <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                        {galleryProducts.map((p) => {
                            const order = galleryIds.indexOf(p.id);
                            const active = order >= 0;
                            return (
                                <button
                                    key={p.id}
                                    type="button"
                                    onClick={() => toggleGalleryProduct(p.id)}
                                    data-testid={`homepage-gallery-toggle-${p.id}`}
                                    className={`relative text-left border transition-colors group ${
                                        active ? "border-melilux-gold" : "border-white/10 hover:border-white/30"
                                    }`}
                                >
                                    <div className={`aspect-square bg-melilux-surface ${active ? "" : "opacity-60 group-hover:opacity-90"} transition-opacity`}>
                                        {p.images?.[0] && (
                                            <img src={resolveImg(p.images[0])} alt="" loading="lazy" className="w-full h-full object-cover" />
                                        )}
                                    </div>
                                    {active && (
                                        <span className="absolute top-2 left-2 flex items-center gap-1 bg-melilux-gold text-melilux-bg px-2 py-1 text-[0.6rem] uppercase tracking-[0.2em] font-medium">
                                            <Check size={11} strokeWidth={2.5} /> {order + 1}
                                        </span>
                                    )}
                                    <p className="px-2 py-2 text-xs truncate text-melilux-cream/80">{p.name}</p>
                                </button>
                            );
                        })}
                    </div>
                )}
            </section>
        </div>
    );
}

function ImagePicker({ label, value, onChange, aspect = "aspect-[4/3]", testid, t }) {
    const [uploading, setUploading] = useState(false);
    const [browserOpen, setBrowserOpen] = useState(false);

    const upload = async (files) => {
        if (!files?.length) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", files[0]);
            const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
            onChange(data.url);
            setBrowserOpen(false);
        } catch (err) {
            toast.error(formatError(err?.response?.data?.detail) || "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    return (
        <div data-testid={testid}>
            <p className="text-[0.65rem] uppercase tracking-[0.28em] text-melilux-cream/55">{label}</p>
            <div className={`mt-3 ${aspect} max-w-sm bg-melilux-surface border border-white/10 overflow-hidden flex items-center justify-center`}>
                {value ? (
                    <img src={resolveImg(value, 480)} alt="" className="w-full h-full object-cover" />
                ) : (
                    <ImageOff size={22} strokeWidth={1} className="text-melilux-cream/20" />
                )}
            </div>
            <div className="mt-3 flex items-center gap-3 flex-wrap">
                <label className="cursor-pointer border border-white/15 px-4 py-2 text-[0.65rem] uppercase tracking-[0.25em] hover:border-melilux-gold hover:text-melilux-gold transition-colors">
                    {uploading ? t("admin.home.images.uploading") : t("admin.home.images.upload")}
                    <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => { upload(e.target.files); e.target.value = ""; }}
                        data-testid={testid ? `${testid}-upload` : undefined}
                    />
                </label>
                <button
                    onClick={() => setBrowserOpen(true)}
                    data-testid={testid ? `${testid}-browse` : undefined}
                    className="border border-white/15 px-4 py-2 text-[0.65rem] uppercase tracking-[0.25em] hover:border-melilux-gold hover:text-melilux-gold transition-colors"
                >
                    {t("admin.home.images.fromProduct")}
                </button>
                {value && (
                    <button
                        onClick={() => onChange("")}
                        data-testid={testid ? `${testid}-remove` : undefined}
                        className="inline-flex items-center gap-1.5 text-[0.65rem] uppercase tracking-[0.25em] text-melilux-cream/55 hover:text-destructive transition-colors"
                    >
                        <X size={12} strokeWidth={1.5} /> {t("admin.home.images.remove")}
                    </button>
                )}
            </div>

            <ProductImagePicker
                open={browserOpen}
                onClose={() => setBrowserOpen(false)}
                onPick={(img) => { onChange(img); setBrowserOpen(false); }}
            />
        </div>
    );
}
